<script lang="ts" setup>
import { SancareBreadcrumb } from '@sancare/typescript-commons'
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import GroupingCardSmr from '@/stay-displayer/grouping/smr/GroupingCardSmr.vue'
import ReadingListNavigator from '@/stay-displayer/ReadingListNavigator.vue'
import { useReadingListMode } from '@/stay-displayer/smr/composables/useReadingListMode'
import { refreshStay } from '@/stay-displayer/smr/dataRefreshers'
import GeneralStayData from '@/stay-displayer/smr/GeneralStayData.vue'
import RhsSummaries from '@/stay-displayer/smr/rhs/RhsSummaries.vue'
import RhsTable from '@/stay-displayer/smr/RhsTable.vue'
import SmrLoadingError from '@/stay-displayer/smr/SmrLoadingError.vue'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { HospitalizationType, SmrLoadingErrorReason } from '@/store/modules/stay/smr/types'

const route = useRoute()
const router = useRouter()

const store = useStore()

const props = defineProps<{
  stayId: string
  idType: string
}>()

const refreshErrorReasons = { ...SmrLoadingErrorReason }
const refreshErrorReason: Ref<SmrLoadingErrorReason> = ref(null)
const isLoading: Ref<boolean> = ref(false)

watch(() => props.stayId, () => {
  refresh()
})
const currentStay = computed(() => store.state.smrStay.currentStay)

const { breadcrumbItems } = useBreadcrumb()

// Canonicalize the URL: make sure ?stayLabel=... matches the administrativeLocalStayId
// so the breadcrumb can render the right number without reading the store.
watch(currentStay, (stay) => {
  const stayLabel = stay?.administrativeLocalStayId
  if (stayLabel !== null && stayLabel !== undefined && String(route.query.stayLabel) !== String(stayLabel)) {
    router.replace({ query: { ...route.query, stayLabel: String(stayLabel) } })
  }
}, { immediate: true })

const isRefreshed = computed(() => store.state.smrStay.requests.fetchStay.ok)
const isStandardGmeSet = computed(() => currentStay.value.standardModeGroupingResult.gme !== null)
const hideCmgnRow = computed(() => currentStay.value.hospitalizationType === HospitalizationType.COMPLETE)
const mode = computed(() => route.query.mode)
// TODO: To be updated when modes are implemented
const displayedGroupingResult = computed(() => isStandardGmeSet.value
  ? currentStay.value.standardModeGroupingResult
  : currentStay.value.firstStandardModeGroupingResult)
const stayMoneyGap = computed(() => isStandardGmeSet.value
  ? currentStay.value.standardModeGroupingResult.value - currentStay.value.firstStandardModeGroupingResult.value
  : 0)

async function refresh() {
  isLoading.value = true
  try {
    await refreshStay(props.stayId, props.idType, store)
  } catch (e) {
    refreshErrorReason.value = Object.values(refreshErrorReasons).includes(e.message) ? e.message : null
  }
  isLoading.value = false
}

onMounted(async () => {
  await refresh()
})
useReadingListMode('rhs')
</script>

<template>
  <div class="container">
    <div
      v-if="isLoading"
      class="row align-items-center mt-5"
    >
      <div class="col-sm-12">
        <div
          class="loader loader-md loader-center mb-2"
        />
        <div
          class="text-center fst-italic"
        >
          Chargement des informations du SMR...
        </div>
      </div>
    </div>
    <template v-if="isRefreshed">
      <div class="row">
        <div class="col-lg-7 col-md-12">
          <sancare-breadcrumb :items="breadcrumbItems" />
        </div>
        <div class="col-lg-5 col-md-12">
          <reading-list-navigator
            v-if="currentStay"
            :mode
            :current-stay-id="currentStay.id"
            :pmsi-type="PmsiTypeEnum.SMR"
          />
        </div>
      </div>
      <div class="row mb-5">
        <div class="col-lg-7 col-md-12">
          <general-stay-data
            :stay="currentStay"
          />
        </div>
        <div class="col-lg-5 col-md-12">
          <div class="row">
            <div class="col p-0">
              <grouping-card-smr
                :grouping-result="displayedGroupingResult"
                :money-gap="stayMoneyGap"
                :hospitalization-type="currentStay.hospitalizationType"
                :show-validator="true"
              />
            </div>
          </div>
        </div>
      </div>
      <rhs-table
        :rhs-list="currentStay.rhsList"
        :hide-cm-gn-row="hideCmgnRow"
      />
      <rhs-summaries
        :stay="currentStay"
      />
    </template>
    <smr-loading-error
      v-else
      :reason="refreshErrorReason"
      :id-type="idType"
      :stay-id="stayId"
    />
  </div>
</template>
